/**
 * Minimal ethers-v6-shaped wallet for @nktkas/hyperliquid signing.
 * Calls eth_signTypedData_v4 on the EIP-1193 provider directly — avoids
 * ethers BrowserProvider (eth_blockNumber / RPC fetch failures in MetaMask).
 */
export function createEip1193EthersWallet(provider, fromAddress) {
  const address = fromAddress;
  return {
    async getAddress() {
      return address;
    },
    async signTypedData(domain, types, message) {
      if (!provider?.request) throw new Error('Wallet does not support signing');
      const primaryType = Object.keys(types)[0];
      const typedData = {
        domain,
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
            { name: 'chainId', type: 'uint256' },
            { name: 'verifyingContract', type: 'address' },
          ],
          ...types,
        },
        primaryType,
        message,
      };
      return provider.request({
        method: 'eth_signTypedData_v4',
        params: [address, JSON.stringify(typedData)],
      });
    },
  };
}

function isUserRejection(err) {
  const raw = String(err?.message || err?.cause?.message || err || '').toLowerCase();
  return err?.code === 4001 || /user rejected|denied|cancelled|canceled/i.test(raw);
}

/** Plain-English errors for Hyperliquid L1 order signing. */
export function formatL1SignError(err) {
  if (isUserRejection(err)) return 'Signature cancelled in wallet.';

  const cause = String(err?.cause?.message || err?.cause?.shortMessage || '').trim();
  const raw = String(err?.message || err || '').trim();
  const combined = `${raw} ${cause}`.toLowerCase();

  if (/must match the active chainid|provided chainid.*1337/i.test(combined)) {
    return (
      'MetaMask cannot sign Hyperliquid trade orders directly. HL uses signing chain 1337, which is not a real network — ' +
      'there is no working RPC to add in MetaMask (Arbitrum RPC will not work). ' +
      'Use Rabby with the same 0x address, trade on app.hyperliquid.xyz, or wait for Omni agent signing (same pattern as oBot). ' +
      'Your $5.50 balance is fine — this is a wallet/signing limitation only.'
    );
  }

  if (/failed to fetch|could not coalesce|eth_blocknumber|network error/i.test(combined)) {
    return (
      'MetaMask could not connect for the trade signature. Unlock MetaMask, refresh the page, and retry. ' +
      'This step is signature-only — no ETH or network switch required.'
    );
  }

  if (raw.includes('Failed to sign typed data')) {
    return (
      'Could not sign the Hyperliquid order in your wallet. ' +
      (cause || 'Approve the signature popup in MetaMask (same 0x as Omni).')
    );
  }

  return cause || raw || 'Signing failed';
}
